import { useState, useEffect, useRef } from "react";
import { buildServiceUrl, getSortByScore } from "./utils";

import type {
  FetchDataOptions, QueryObject, SetTabsOptions, TabLists, TabData
} from "../../../site/parts/listSearch/type.d";

interface UseListSearchDataProps {
  cardBaseId: string;
  filterFromParams: string[];
  groupByField?: string;
  paginationSize: number;
  queryObject: QueryObject;
  searchQueryFromParams?: string;
  serviceUrl: string;
  tabsData?: TabData[];
  useTabs: boolean;
  useNormerendeCollection?: boolean;
  getFilterQuery: () => string;
}

/**
 * Hook that manages list/tab data fetching, pagination, sorting,
 * search input state, and focus management after "load more".
 */
export function useListSearchData(props: UseListSearchDataProps) {
  const {
    cardBaseId,
    filterFromParams,
    groupByField,
    paginationSize,
    queryObject,
    searchQueryFromParams,
    serviceUrl,
    tabsData,
    useTabs,
    useNormerendeCollection,
    getFilterQuery
  } = props;

  const [isInitialPageLoad, setIsInitialPageLoad] = useState(true);
  const [isFetching, setIsFetching] = useState(false);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const [list, setList] = useState([]);
  const prevCountRef = useRef(0);
  const shouldFocusRef = useRef(false);

  const [tabsLists, setTabsLists] = useState<TabLists[]>([]);
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  const [currentTotalCount, setCurrentTotalCount] = useState(0);
  const [currentQueryObject, setCurrentQueryObject] = useState(queryObject);

  // ── Core fetch ──────────────────────────────────────────────

  /**
   * Fetches data from the service URL with the given query object and options.
   * Manages loading spinner state and handles errors gracefully.
   */
  async function fetchData(
    qo: QueryObject,
    options: FetchDataOptions = {},
    displaySpinner = true
  ) {
    if (displaySpinner) {
      setIsFetching(true);
    }

    try {
      const url = buildServiceUrl(serviceUrl, qo, groupByField, useNormerendeCollection, options);

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch list data. Status: ${response.status}`);
      }

      const data = await response.json();
      setIsInitialPageLoad(false);
      return data;
    } catch (error) {
      setIsInitialPageLoad(false);
      console.error("Error fetching data:", error);
      return [];
    } finally {
      if (displaySpinner) {
        setIsFetching(false);
      }
    }
  }

  // ── Tab helpers ─────────────────────────────────────────────

  /**
   * Updates a single tab's list, total count, or query object by tab ID.
   */
  const updateTab = (
    listId: string | undefined | number,
    options: SetTabsOptions = {}
  ) => {
    const { newList, newTotalCount, newQueryObject } = options;

    setTabsLists((prev) => prev.map((tab) => {
      if (listId === tab.tabId) {
        return {
          ...tab,
          list: newList ?? tab.list,
          tabQuery: newQueryObject ?? tab.tabQuery,
          totalCount: newTotalCount ?? tab.totalCount
        };
      }
      return tab;
    }));
  };

  // ── Shared fetch-and-update ─────────────────────────────────

  /**
   * Fetches data for all tabs or for the single list, then updates state.
   * Centralises the "if tabs → parallel fetch; else → single fetch" pattern.
   *
   * @param buildOptions - Builds FetchDataOptions, receives the tab's query object (or currentQueryObject for non-tab mode).
   * @param overrideQuery - Optional query object override for non-tab mode.
   */
  const fetchAndUpdate = async(
    buildOptions: (qo: QueryObject) => FetchDataOptions,
    overrideQuery?: QueryObject
  ) => {
    if (tabsLists.length > 0) {
      const promises = tabsLists.map((tab) => fetchData(tab.tabQuery, buildOptions(tab.tabQuery)));
      const results = await Promise.all(promises);
      results.forEach((result, index) => updateTab(tabsLists[index]?.tabId, {
        newList: result.list,
        newTotalCount: result?.totalCount
      }));
    } else {
      const qo = overrideQuery ?? currentQueryObject;
      const data = await fetchData(qo, buildOptions(qo));
      setList(data?.list);
      setCurrentTotalCount(data?.totalCount);
    }
  };

  // ── Fetch actions ───────────────────────────────────────────

  /**
   * Fetches results when the user types in the search input.
   * Applies score-based sorting when a search term is present.
   */
  const fetchListOnSearchInput = async(query: string) => {
    const filterQuery = filterFromParams ? getFilterQuery() : "";
    const score = getSortByScore(query, groupByField);

    await fetchAndUpdate(() => ({
      searchTerm: query,
      filterQuery,
      sortByScore: score
    }));
  };

  /**
   * Fetches the initial list(s) on mount.
   * Initialises tabs if configured, otherwise fetches a single list.
   */
  const fetchInitialList = async() => {
    const filterQuery = filterFromParams ? getFilterQuery() : "";

    if (useTabs && tabsData?.length) {
      const promises = tabsData.map((tabData) => fetchData(tabData.tabQuery, { filterQuery }));
      const data = await Promise.all(promises);
      const initTabsList = tabsData.map((tab, index) => ({
        tabId: tab.tabId,
        title: tab.title,
        list: data[index]?.list,
        tabQuery: tab.tabQuery,
        totalCount: data[index]?.totalCount
      }));

      setTabsLists(initTabsList);
      setActiveTabIndex(0);
    } else {
      const results = await fetchData(queryObject, { filterQuery });
      setList(results?.list);
      setCurrentTotalCount(results?.totalCount);
    }
  };

  /**
   * Re-fetches results with the current (or cleared) filter applied.
   * @param emptyFilter - When true, ignores current filter selections.
   */
  const fetchWithFilter = async(emptyFilter?: boolean): Promise<void> => {
    const filterQuery = emptyFilter ? "" : getFilterQuery();

    await fetchAndUpdate(() => ({
      searchTerm: searchQuery,
      filterQuery
    }));
  };

  /**
   * Loads the next page of results (pagination).
   * Sets up focus tracking so the first new item receives focus.
   * @param id - Tab ID when using tabs; omit for single-list mode.
   */
  const handleFetchMore = async(id?: string) => {
    const score = getSortByScore(searchQuery, groupByField);
    const options: FetchDataOptions = {
      searchTerm: searchQuery,
      filterQuery: getFilterQuery(),
      sortByScore: score
    };

    shouldFocusRef.current = true;

    if (useTabs && id !== "") {
      const tab = tabsLists.find((t) => t.tabId === id);
      prevCountRef.current = tab?.list.length ?? 0;

      const nextQuery = {
        ...tab?.tabQuery,
        count: paginationSize,
        start: tab?.list?.length ?? 0
      };

      const data = await fetchData(nextQuery, options, false);
      updateTab(id, {
        newList: [...(tab?.list ?? []), ...data.list],
        newTotalCount: data.totalCount
      });
    } else {
      prevCountRef.current = list?.length ?? 0;

      const nextQuery = {
        ...currentQueryObject,
        count: paginationSize,
        start: list?.length ?? 0
      };

      const data = await fetchData(nextQuery, options, false);
      setList((prev) => [...prev, ...data.list]);
      setCurrentTotalCount(data?.totalCount);
    }
  };

  /**
   * Re-fetches results after the user changes the sort order.
   * Updates the stored query object so subsequent fetches keep the new sort.
   */
  const handleSortSelected = async(sortByValue: string) => {
    if (tabsLists.length > 0) {
      const nextTabs = tabsLists.map((tab) => ({
        ...tab,
        tabQuery: { ...tab.tabQuery, sort: sortByValue }
      }));

      const data = await Promise.all(
        nextTabs.map((tab) => fetchData(tab.tabQuery, {
          searchTerm: searchQuery,
          filterQuery: getFilterQuery()
        }))
      );

      nextTabs.forEach((tab, index) => {
        updateTab(tab.tabId, {
          newList: data[index]?.list,
          newTotalCount: data[index]?.totalCount,
          newQueryObject: tab.tabQuery
        });
      });
    } else {
      const nextQuery = { ...currentQueryObject, sort: sortByValue };
      const data = await fetchData(nextQuery, {
        searchTerm: searchQuery,
        filterQuery: getFilterQuery()
      });
      setList(data?.list);
      setCurrentTotalCount(data?.totalCount);
      setCurrentQueryObject(nextQuery);
    }
  };

  // ── Focus management ────────────────────────────────────────

  /** After "load more", focuses the first newly added card for keyboard users. */
  useEffect(() => {
    if (!shouldFocusRef.current) return;

    const currentList = useTabs
      ? tabsLists[activeTabIndex]?.list
      : list;

    const oldIndex = prevCountRef.current;
    if (currentList?.length > oldIndex) {
      document
        .querySelector<HTMLElement>(`[data-item-id="${cardBaseId}-${oldIndex}"]`)
        ?.focus();
    }
    shouldFocusRef.current = false;
  }, [list, tabsLists, activeTabIndex]);

  // ── Initial load ────────────────────────────────────────────

  /** Runs once on mount: restores search from URL params or fetches initial data. */
  useEffect(() => {
    if (searchQueryFromParams) {
      fetchListOnSearchInput(searchQueryFromParams);
      setSearchQuery(searchQueryFromParams);
      if (searchInputRef.current) {
        searchInputRef.current.value = searchQueryFromParams;
      }
    } else {
      fetchInitialList();
    }
  }, []);

  return {
    // State
    isInitialPageLoad,
    isFetching,
    list,
    tabsLists,
    activeTabIndex,
    currentTotalCount,
    searchQuery,
    searchInputRef,

    // Actions
    setSearchQuery,
    setActiveTabIndex,
    fetchListOnSearchInput,
    fetchWithFilter,
    handleFetchMore,
    handleSortSelected
  };
}
